import { format } from 'date-fns'
import { Guest, Role } from 'interfaces'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material'
import { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'

interface GuestProps {
  guests: Guest[]
}

interface GuestDataProps {
  guests: Guest[]
  individualChecked: {
    invitedGuests: boolean
    waitingGuests: boolean
    activeGuests: boolean
    onlyActiveStatusGuests: boolean
    allActiveGuests: boolean
  }
}

function guestData({ guests, individualChecked }: GuestDataProps) {
  const { i18n } = useTranslation()
  const headers = {
    en: [
      'Greg Person ID',
      'Name',
      'Guest Role ID',
      'Guest Role',
      'Unit',
      'Start date',
      'End date',
      'Host',
      'Comments',
    ],
    nb: [
      'Greg-person-ID',
      'Navn',
      'Gjesterolle-ID',
      'Gjesterolle',
      'Avdeling',
      'Startdato',
      'Sluttdato',
      'Vert',
      'Kommentarer',
    ],
  }
  const header = headers[i18n.language === 'en' ? 'en' : 'nb']

  const buildRow = (guest: Guest, role: Role) => [
    guest.pid,
    `${guest.first} ${guest.last}`,
    role.id,
    i18n.language === 'en' ? role.name_en : role.name_nb,
    i18n.language === 'en' ? role.ou_en : role.ou_nb,
    role.start_date ? format(role.start_date, 'yyyy-MM-dd') : '',
    format(role.end_date, 'yyyy-MM-dd'),
    role.sponsor_name ?? '',
    role.comments ?? '',
  ]

  const sortGuests = (a: Guest, b: Guest) => {
    if (a.first === b.first) return a.last.localeCompare(b.last)
    return a.first.localeCompare(b.first)
  }
  const invitedGuests = guests
    .filter((person) => !person.registered)
    .sort(sortGuests)
  const waitingGuests = guests
    .filter((person) => person.registered && !person.verified)
    .sort(sortGuests)
  const activeGuests = guests
    .filter((person) => person.verified)
    .sort(sortGuests)

  const guestList = []
  if (individualChecked.invitedGuests) {
    guestList.push(...invitedGuests)
  }
  if (individualChecked.waitingGuests) {
    guestList.push(...waitingGuests)
  }

  const today = new Date()
  const rows = [header]

  const guestRows = guestList.flatMap((guest: Guest) =>
    guest.roles.map((role: Role) => buildRow(guest, role))
  )
  rows.push(...guestRows)

  if (individualChecked.activeGuests) {
    const activeGuestRows = individualChecked.onlyActiveStatusGuests
      ? activeGuests.flatMap((guest: Guest) =>
          guest.roles
            .filter((role: Role) => role.end_date >= today)
            .map((role: Role) => buildRow(guest, role))
        )
      : activeGuests.flatMap((guest: Guest) =>
          guest.roles.map((role: Role) => buildRow(guest, role))
        )
    rows.push(...activeGuestRows)
  }

  return rows
}

function DownloadGuestsButton({ guests }: GuestProps) {
  const initialState = {
    invitedGuests: false,
    waitingGuests: false,
    activeGuests: false,
    onlyActiveStatusGuests: false,
    allActiveGuests: false,
  }
  const [openDialogBox, setOpenDialogBox] = useState<boolean>(false)
  const [individualChecked, setIndividualChecked] = useState(initialState)
  const { t, i18n } = useTranslation('common')

  const boxesChecked = Object.values(individualChecked).some(
    (value) => value === true
  )
  const activeGuestChoiceChecked =
    !individualChecked.activeGuests ||
    (individualChecked.activeGuests &&
      (individualChecked.onlyActiveStatusGuests ||
        individualChecked.allActiveGuests))

  const activeGuestsChecked = individualChecked.activeGuests

  const handleIndividualCheckedChange = (event: any, name: string) => {
    setIndividualChecked({
      ...individualChecked,
      [name]: event.target.checked,
    })
  }

  const openAndReset = () => {
    setOpenDialogBox(true)
    setIndividualChecked(initialState)
  }

  const makeFileName = () => {
    const dformat = format(new Date(), 'yyyy-MM-dd')

    const isEnglish = i18n.language === 'en'
    const labels = isEnglish
      ? {
          base: 'guests',
          invited: 'invited',
          waiting: 'waiting',
          allActive: 'all_confirmed',
          active: 'active',
        }
      : {
          base: 'gjester',
          invited: 'invitert',
          waiting: 'venter',
          allActive: 'alle_godkjente',
          active: 'aktive',
        }

    const parts = [labels.base]
    if (individualChecked.invitedGuests) parts.push(labels.invited)
    if (individualChecked.waitingGuests) parts.push(labels.waiting)
    if (individualChecked.allActiveGuests) parts.push(labels.allActive)
    else if (individualChecked.activeGuests) parts.push(labels.active)

    return `${parts.join('_')}_${dformat}.csv`
  }

  return (
    <Box textAlign="center" sx={{ mb: '2rem' }}>
      <Button
        onClick={() => openAndReset()}
        sx={{ borderColor: 'black', color: 'black' }}
      >
        {t('button.downloadGuestData')}
      </Button>

      <Dialog open={openDialogBox} onClose={() => setOpenDialogBox(false)}>
        <DialogTitle>{t('download.title')}</DialogTitle>
        <DialogContent sx={{ width: '30rem', height: '20rem' }}>
          <DialogContentText sx={{ mb: '1rem' }}>
            {t('download.bodyText')}
          </DialogContentText>
          <FormGroup>
            {[
              { name: 'invitedGuests', label: t('download.invitedGuests') },
              { name: 'waitingGuests', label: t('download.waitingGuests') },
              { name: 'activeGuests', label: t('download.activeGuests') },
            ].map(({ name, label }) => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    checked={
                      individualChecked[name as keyof typeof individualChecked]
                    }
                    onChange={(e) => handleIndividualCheckedChange(e, name)}
                  />
                }
                label={label}
              />
            ))}
            {activeGuestsChecked && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  {
                    name: 'onlyActiveStatusGuests',
                    label: t('download.onlyActive'),
                  },
                  { name: 'allActiveGuests', label: t('download.allGuests') },
                ].map(({ name, label }) => (
                  <FormControlLabel
                    key={name}
                    sx={{ ml: '2rem' }}
                    control={
                      <Checkbox
                        checked={
                          individualChecked[
                            name as keyof typeof individualChecked
                          ]
                        }
                        onChange={(e) => handleIndividualCheckedChange(e, name)}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialogBox(false)} sx={{ mr: '1rem' }}>
            {t('button.cancel')}
          </Button>
          <CSVLink
            filename={makeFileName()}
            data={guestData({ guests, individualChecked })}
          >
            <Button
              disabled={!boxesChecked || !activeGuestChoiceChecked}
              onClick={() => setOpenDialogBox(false)}
            >
              {t('button.download')}
            </Button>
          </CSVLink>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DownloadGuestsButton
